import { useCallback, useEffect, useState } from "react";
import { fetchProducts } from "@/store/api/products";
import { fetchAvailableCoupons } from "@/store/api/coupons";
import { fetchPurchaseHistory } from "@/store/api/history";
import { fetchPurchasedProducts } from "@/store/api/purchased";
import { fetchLoggedInUser } from "@/store/api/loggedInUser";
import { fetchUnclaimedBundlePurchases } from "@/store/api/bundleSync";
import { readContextPartyId } from "@/store/api/partyContext";
import { computeCouponStats, getActiveCoupon } from "@/store/coupons";
import type { Coupon, CouponStats, HistoryRow, Product, BundlePurchaseRow } from "@/store/types";

/** Case-insensitive read of a URL query param (iMIS params are PascalCase: ?Category=, ?Product=). */
function getUrlParam(name: string): string | null {
  const match = new RegExp("[?&]" + name + "=([^&]*)", "i").exec(location.search);
  return match ? decodeURIComponent(match[1].replace(/\+/g, " ")) : null;
}

export interface StoreData {
  loading: boolean;
  /** Set when loading fails, or when the (filtered) query returns no products. */
  error: string | null;
  products: Product[];
  coupons: Coupon[];
  history: HistoryRow[];
  stats: CouponStats;
  activeCoupon: Coupon | null;
  /** Whether to show member pricing (true) or non-member/standard pricing (false). */
  isMember: boolean;
  /** Product codes the member has already purchased (from history). */
  purchasedCodes: Set<string>;
  /** Bundle purchases from today not yet represented in AGD_Coupons — drives the Claim button. */
  unclaimedBundlePurchases: BundlePurchaseRow[];
  category: string | null;
  product: string | null;
  /** True when a category or product URL filter is active (controls the "Back to store" link). */
  isFiltered: boolean;
  /** True when a single product was requested → detail layout. */
  singleMode: boolean;
  /** True when there's an authenticated party (coupons/history/member pricing are available). */
  isLoggedIn: boolean;
  refetch: () => Promise<void>;
}

const EMPTY_STATS: CouponStats = { total: 0, used: 0, remaining: 0 };

export function useStoreData(): StoreData {
  const category = getUrlParam("category");
  const product = getUrlParam("product");
  const isFiltered = !!(category || product);
  const singleMode = !!product;
  const isLoggedIn = !!readContextPartyId();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [purchasedCodes, setPurchasedCodes] = useState<Set<string>>(new Set());
  const [unclaimedBundlePurchases, setUnclaimedBundlePurchases] = useState<BundlePurchaseRow[]>([]);
  const [stats, setStats] = useState<CouponStats>(EMPTY_STATS);
  const [isMember, setIsMember] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const partyId = readContextPartyId();

      const [productList, couponList, user, unclaimedBundles] = await Promise.all([
        fetchProducts({ category, product }),
        partyId ? fetchAvailableCoupons(partyId) : Promise.resolve([]),
        partyId ? fetchLoggedInUser() : Promise.resolve(null),
        partyId ? fetchUnclaimedBundlePurchases(partyId) : Promise.resolve([]),
      ]);
      // History is filtered by the loaded coupons, so it runs after they resolve.
      const historyList = partyId ? await fetchPurchaseHistory(partyId, couponList) : [];
      const purchasedList = partyId ? await fetchPurchasedProducts(partyId) : [];

      setCoupons(couponList);
      setStats(computeCouponStats(couponList));
      setHistory(historyList);
      setPurchasedCodes(new Set(purchasedList.map((p) => p.ProductCode)));
      setUnclaimedBundlePurchases(unclaimedBundles);
      setProducts(productList);
      // Default to non-member pricing when the query returns no row (or there's no session).
      setIsMember(user?.isMember ?? false);

      if (productList.length === 0) {
        if (product) setError("Product not found.");
        else if (category) setError("No products found in this category.");
        else setError("No products could be loaded. Please try again later.");
      }
    } catch (e) {
      console.error(e);
      setError("An unexpected error occurred while loading the page.");
    } finally {
      setLoading(false);
    }
  }, [category, product]);

  useEffect(() => {
    load();
  }, [load]);

  const activeCoupon = getActiveCoupon(coupons);

  return {
    loading,
    error,
    products,
    coupons,
    history,
    stats,
    activeCoupon,
    isMember,
    purchasedCodes,
    unclaimedBundlePurchases,
    category,
    product,
    isFiltered,
    singleMode,
    isLoggedIn,
    refetch: load,
  };
}
